import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CommentIcon from '@mui/icons-material/Comment';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import NotificationIcon from '@mui/icons-material/Notifications';
import cx from 'classnames';
import BuildInfo from 'Components/BuildInfo/BuildInfo';
import ProfileModal from 'Components/ProfileModal/ProfileModal';
import OrganisationSelector from 'Components/OrganizationSelector/OrganizationSelector';
import SideMenuLiftDetail from 'Components/SideMenuLiftDetail/SideMenuLiftDetail';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import LiftIcon from '../../Assets/LiftIcon';
import classes from './SideMenu.module.scss';
import { logout } from 'Utils/auth';
import { useUser } from 'Providers/UserProvider';

const drawerWidth = 220;

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : theme.spacing(7),
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: open ? drawerWidth : theme.spacing(7),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

interface ISideMenuProps {
  mobileOpen?: boolean;
  drawerOpen?: boolean;
  onMobileMenuToggle?: () => void;
  onDrawerToggle?: () => void;
}

const SideMenu = (props: ISideMenuProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const location = useLocation();
  const selectedRoute = location?.pathname?.replace('/', '');

  const [openProfileModal, setOpenProfileModal] = React.useState(false);

  const user = useUser();

  // Use state from props
  const mobileOpen = props.mobileOpen || false;
  const drawerState = props.drawerOpen !== undefined ? props.drawerOpen : true;
  const handleDrawerToggle = props.onMobileMenuToggle || (() => {});
  const handleDesktopDrawerToggle = props.onDrawerToggle || (() => {});

  const handleDrawerOpen = () => {
    if (isMobile) {
      handleDrawerToggle();
    } else {
      handleDesktopDrawerToggle();
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      handleDrawerToggle();
    } else {
      handleDesktopDrawerToggle();
    }
  };

  const drawerContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className={classes.header}>
        {(drawerState && !isMobile) || (mobileOpen && isMobile) ? (
          <React.Fragment>
            <Typography
              variant="h6"
              component="h3"
              className={classes.headerTitle}
            >
              <FormattedMessage id="liftstatus" />
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon className={classes.white} />
            </IconButton>
          </React.Fragment>
        ) : (
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleDrawerOpen}
            className={cx(
              classes.menuButton,
              (drawerState || mobileOpen) && classes.menuButtonHidden
            )}
          >
            <MenuIcon className={classes.white} />
          </IconButton>
        )}
      </div>

      <List className={classes.menuItems}>
        {!user.isMechanic && (
          <>
            {(drawerState || isMobile) && (
              <div className={classes.ogSelect}>
                <React.Suspense>
                  <OrganisationSelector />
                </React.Suspense>
              </div>
            )}

            <Link to="/dashboard" className={classes.link}>
              <ListItemButton
                className={
                  selectedRoute === 'dashboard'
                    ? classes.selectedRoute
                    : undefined
                }
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon>
                  <HomeIcon className={classes.icon} />
                </ListItemIcon>
                <ListItemText
                  disableTypography={true}
                  className={classes.itemText}
                  primary={<FormattedMessage id="dashboard" />}
                />
              </ListItemButton>
            </Link>
            <Link to="/alarm-calls" className={classes.link}>
              <ListItemButton
                className={
                  selectedRoute === 'alarm-calls'
                    ? classes.selectedRoute
                    : undefined
                }
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon>
                  <NotificationIcon className={classes.icon} />
                </ListItemIcon>
                <ListItemText
                  disableTypography={true}
                  className={classes.itemText}
                  primary={<FormattedMessage id="calls" />}
                />
              </ListItemButton>
            </Link>
            {!user.isClientLight && !user.isMechanic ? (
              <Link to="/current-calls" className={classes.link}>
                <ListItemButton
                  className={
                    selectedRoute === 'current-calls'
                      ? classes.selectedRoute
                      : undefined
                  }
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <NotificationImportantIcon className={classes.icon} />
                  </ListItemIcon>
                  <ListItemText
                    disableTypography={true}
                    className={classes.itemText}
                    primary={<FormattedMessage id="current.calls" />}
                  />
                </ListItemButton>
              </Link>
            ) : (
              <></>
            )}
            {!user.isClientLight && !user.isMechanic && (
              <Link to="/events" className={classes.link}>
                <ListItemButton
                  className={
                    selectedRoute === 'events'
                      ? classes.selectedRoute
                      : undefined
                  }
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <CommentIcon className={classes.icon} />
                  </ListItemIcon>
                  <ListItemText
                    disableTypography={true}
                    className={classes.itemText}
                    primary={<FormattedMessage id="events" />}
                  />
                </ListItemButton>
              </Link>
            )}
            <Link to="/lifts" className={classes.link}>
              <ListItemButton
                className={
                  selectedRoute.includes('lifts')
                    ? classes.selectedRoute
                    : undefined
                }
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon>
                  <LiftIcon className={classes.icon} />
                </ListItemIcon>
                <ListItemText
                  disableTypography={true}
                  className={classes.itemText}
                  primary={<FormattedMessage id="lifts" />}
                />
              </ListItemButton>
            </Link>
          </>
        )}

        <div className={classes.footer}>
          <ListItemButton
            onClick={() => setOpenProfileModal(true)}
            className={classes.account}
          >
            <ListItemIcon>
              <AccountCircleIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText
              disableTypography={true}
              className={classes.accountName}
              primary={user.username}
            />
          </ListItemButton>
          <BuildInfo versionOnly={!drawerState && !isMobile} />
        </div>
      </List>
      <SideMenuLiftDetail />
    </div>
  );

  if (isMobile) {
    // Mobile layout: only the drawer and modal, menu button handled by Dashboard
    return (
      <>
        <CssBaseline />
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <ProfileModal
          open={openProfileModal}
          setOpen={setOpenProfileModal}
          onConfirm={logout}
        />
      </>
    );
  }

  // Desktop layout: just the drawer components without layout wrapper
  return (
    <>
      <CssBaseline />
      <StyledAppBar position="absolute" open={drawerState} />

      {/* Desktop drawer */}
      <StyledDrawer
        variant="permanent"
        open={drawerState}
        className={cx(!drawerState && classes.toolbarClosed)}
        style={{ height: '100%' }}
      >
        {drawerContent}
      </StyledDrawer>

      <ProfileModal
        open={openProfileModal}
        setOpen={setOpenProfileModal}
        onConfirm={logout}
      />
    </>
  );
};

export default SideMenu;
